import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { Link2, Link2Off, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    Skeleton,
    Text,
} from "@athena/ui";
import {
    EnsureIntegrationRecordsDocument,
    IntegrationRecordsDocument,
    LinkIntegrationRecordDocument,
    UnlinkIntegrationRecordDocument,
} from "@/graphql/integrations.generated";
import type { IntegrationEntityType } from "@athena/common/graphql/generated/schema";
import { reportQueryFailure } from "@/lib/queryErrors";

/**
 * Records browser for one organization's integration.
 *
 * Answers the question the mapping table alone can't: which courses, learners
 * and assignments are *not* yet mirrored in the provider. From here an admin can
 * create the missing provider records (one row or the whole filtered set), or
 * point a row at a record that already exists — the repair path for anything
 * that predates the integration.
 *
 * Ported from manual-rw's Records tabs, generalized: nothing here is
 * Salesforce-specific, and the same three tabs work for any provider that
 * declares the matching capabilities.
 */

const TABS: {
    entityType: IntegrationEntityType;
    label: string;
    /** Column headings; `secondary` splits the record's detail into its own column. */
    columns: { primary: string; secondary?: string };
}[] = [
    { entityType: "COURSE", label: "Courses", columns: { primary: "Course" } },
    { entityType: "USER", label: "Users", columns: { primary: "User", secondary: "Email" } },
    {
        entityType: "ASSIGNMENT",
        label: "Assignments",
        columns: { primary: "Assignment", secondary: "Learner" },
    },
];

/** Plain words for each kind of record — this panel is read by non-technical admins. */
const NOUNS: Record<IntegrationEntityType, { one: string; many: string }> = {
    USER: { one: "learner", many: "learners" },
    COURSE: { one: "course", many: "courses" },
    ASSIGNMENT: { one: "assignment", many: "assignments" },
};

const counted = (entityType: IntegrationEntityType, count: number): string =>
    `${count} ${count === 1 ? NOUNS[entityType].one : NOUNS[entityType].many}`;

/** Names, up to a point — a 200-row backfill must not paste 200 names into a dialog. */
const NAMES_SHOWN = 6;

const nameList = (labels: string[]): string =>
    labels.length <= NAMES_SHOWN
        ? labels.join(", ")
        : `${labels.slice(0, NAMES_SHOWN).join(", ")} and ${labels.length - NAMES_SHOWN} more`;

type Filter = "ALL" | "CONNECTED" | "NOT_CONNECTED";

const FILTERS: { value: Filter; label: string }[] = [
    { value: "ALL", label: "All" },
    { value: "CONNECTED", label: "Connected" },
    { value: "NOT_CONNECTED", label: "Not connected" },
];

/** Matches the server's per-call cap so the UI can't ask for a rejected batch. */
const MAX_BATCH = 200;

function Chip({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-muted-foreground hover:bg-muted"
            }`}
        >
            {children}
        </button>
    );
}

export function IntegrationRecordsPanel({
    organizationId,
    providerLabel = "the provider",
}: {
    organizationId: string;
    /** Product name, so the dialog can say where the records are going. */
    providerLabel?: string;
}) {
    const [
        entityType,
        setEntityType,
    ] = useState<IntegrationEntityType>("COURSE");
    const [
        filter,
        setFilter,
    ] = useState<Filter>("ALL");
    const [
        search,
        setSearch,
    ] = useState("");
    /** Row currently showing its external-id input, and the value being typed. */
    const [
        linking,
        setLinking,
    ] = useState<{ entityId: string; externalId: string } | null>(null);
    const [
        busyEntityId,
        setBusyEntityId,
    ] = useState<string | null>(null);
    const [
        bulkRunning,
        setBulkRunning,
    ] = useState(false);
    /** Rows waiting on confirmation because connecting them creates other records too. */
    const [
        pending,
        setPending,
    ] = useState<string[] | null>(null);

    const { data, loading, refetch } = useQuery(IntegrationRecordsDocument, {
        variables: { organizationId, entityType },
    });
    const [linkRecord] = useMutation(LinkIntegrationRecordDocument);
    const [unlinkRecord] = useMutation(UnlinkIntegrationRecordDocument);
    // A backfill writes lastError on the integration row — and clears it on a clean
    // run — so the card above has to re-read it. Without this the failure banner from
    // an earlier attempt sits there after the retry succeeded, which reads as a fresh
    // error on a row that just connected.
    const [ensureRecords] = useMutation(EnsureIntegrationRecordsDocument, { refetchQueries: ["Integrations"] });

    const records = data?.integrationRecords ?? [];
    const columns = TABS.find((tab) => tab.entityType === entityType)?.columns ?? { primary: "Record" };

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        return records.filter((record) => {
            if (filter === "CONNECTED" && !record.externalId) return false;
            if (filter === "NOT_CONNECTED" && record.externalId) return false;
            if (!term) return true;
            return `${record.label} ${record.detail ?? ""} ${record.externalId ?? ""}`.toLowerCase().includes(term);
        });
    }, [
        records,
        filter,
        search,
    ]);

    const unlinkedVisible = visible.filter((record) => !record.externalId);

    /**
     * The records these rows depend on that the provider doesn't have yet, grouped
     * by kind and deduplicated — twelve assignments on one course must read as one
     * course, not twelve.
     */
    const prerequisites = useMemo(() => {
        if (!pending) return [];
        const wanted = new Set(pending);
        const groups = new Map<IntegrationEntityType, Map<string, string>>();
        for (const record of records) {
            if (!wanted.has(record.entityId)) continue;
            for (const dependency of record.requires) {
                if (dependency.connected) continue;
                const group = groups.get(dependency.entityType) ?? new Map<string, string>();
                group.set(dependency.entityId, dependency.label);
                groups.set(dependency.entityType, group);
            }
        }
        return [...groups.entries()].map(
            ([
                type,
                labels,
            ]) => ({ entityType: type, labels: [...labels.values()] }),
        );
    }, [
        pending,
        records,
    ]);

    const handleLink = async (entityId: string, externalId: string) => {
        setBusyEntityId(entityId);
        try {
            await linkRecord({ variables: { input: { organizationId, entityType, entityId, externalId } } });
            toast.success("Linked.");
            setLinking(null);
            await refetch();
        } catch (error) {
            reportQueryFailure(error, "Could not link the record");
        } finally {
            setBusyEntityId(null);
        }
    };

    const handleUnlink = async (entityId: string) => {
        setBusyEntityId(entityId);
        try {
            await unlinkRecord({ variables: { organizationId, entityType, entityId } });
            toast.success("Unlinked. The provider record was left in place.");
            await refetch();
        } catch (error) {
            reportQueryFailure(error, "Could not unlink the record");
        } finally {
            setBusyEntityId(null);
        }
    };

    /** Creates the provider records for `entityIds`, reporting partial success honestly. */
    const runEnsure = async (entityIds: string[]) => {
        if (entityIds.length === 0) return;
        const batch = entityIds.slice(0, MAX_BATCH);
        setBulkRunning(entityIds.length > 1);
        setBusyEntityId(entityIds.length === 1 ? (entityIds[0] ?? null) : null);
        try {
            const result = await ensureRecords({
                variables: { input: { organizationId, entityType, entityIds: batch } },
            });
            const payload = result.data?.ensureIntegrationRecords;
            if (payload?.ok) toast.success(payload.message);
            else toast.error(payload?.message ?? "Nothing was linked");
            // Say so rather than silently covering only the first page.
            if (entityIds.length > batch.length) {
                toast.info(`${entityIds.length - batch.length} more not attempted — run it again to continue.`);
            }
            await refetch();
        } catch (error) {
            reportQueryFailure(error, "Could not create the provider records");
        } finally {
            setBulkRunning(false);
            setBusyEntityId(null);
        }
    };

    /**
     * Connect, asking first when it cascades.
     *
     * An assignment is an enrollment: it can't exist in the provider without the
     * learner and the course, so one click can create three records. That used to
     * fail halfway with the provider's own wording; now the admin sees the names of
     * everything it will create and decides.
     */
    const requestEnsure = (entityIds: string[]) => {
        const cascades = records.some(
            (record) => entityIds.includes(record.entityId) && record.requires.some((d) => !d.connected),
        );
        if (cascades) setPending(entityIds);
        else void runEnsure(entityIds);
    };

    const pendingRecord = pending?.length === 1 ? records.find((record) => record.entityId === pending[0]) : undefined;

    return (
        <div className="border-border mt-4 flex flex-col gap-3 border-t pt-4">
            <div className="flex flex-wrap items-center gap-2">
                {TABS.map((tab) => (
                    <Chip
                        key={tab.entityType}
                        active={entityType === tab.entityType}
                        onClick={() => {
                            setEntityType(tab.entityType);
                            setLinking(null);
                        }}
                    >
                        {tab.label}
                    </Chip>
                ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                {FILTERS.map((option) => (
                    <Chip key={option.value} active={filter === option.value} onClick={() => setFilter(option.value)}>
                        {option.label}
                    </Chip>
                ))}
                <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search"
                    className="h-8 max-w-xs text-sm"
                />
                {unlinkedVisible.length > 0 ? (
                    <Button
                        size="sm"
                        variant="outline"
                        className="ml-auto w-auto"
                        disabled={bulkRunning}
                        onClick={() => requestEnsure(unlinkedVisible.map((record) => record.entityId))}
                    >
                        <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                        {bulkRunning ? "Connecting…" : `Connect all (${unlinkedVisible.length})`}
                    </Button>
                ) : null}
            </div>

            {loading ? (
                <Skeleton className="h-24 rounded-lg" />
            ) : visible.length === 0 ? (
                <Text className="text-muted-foreground py-4 text-center text-sm">Nothing to show here.</Text>
            ) : (
                <div className="border-border overflow-x-auto rounded-lg border">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-muted-foreground text-xs">
                            <tr>
                                <th className="px-3 py-2 text-left font-medium">{columns.primary}</th>
                                {columns.secondary ? (
                                    <th className="px-3 py-2 text-left font-medium">{columns.secondary}</th>
                                ) : null}
                                <th className="px-3 py-2 text-left font-medium">Status</th>
                                <th className="px-3 py-2 text-right font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((record) => {
                                const busy = busyEntityId === record.entityId;
                                const isLinking = linking?.entityId === record.entityId;
                                return (
                                    <tr key={record.entityId} className="border-border border-t">
                                        <td className="px-3 py-2">
                                            <Text className="text-sm">{record.label}</Text>
                                            {/* Only the two-column tabs split the detail out. */}
                                            {!columns.secondary && record.detail ? (
                                                <Text className="text-muted-foreground text-xs">{record.detail}</Text>
                                            ) : null}
                                        </td>
                                        {columns.secondary ? (
                                            <td className="text-muted-foreground px-3 py-2">{record.detail ?? "—"}</td>
                                        ) : null}
                                        <td className="px-3 py-2">
                                            {/* The provider's id is a debugging detail, not a column —
                                                it sits on the tooltip so the table stays readable. */}
                                            <span
                                                title={record.externalId ?? undefined}
                                                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                                                    record.externalId
                                                        ? "border-success/30 bg-success/10 text-success"
                                                        : "border-border bg-muted text-muted-foreground"
                                                }`}
                                            >
                                                {record.externalId ? "Connected" : "Not connected"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            {isLinking ? (
                                                <div className="flex items-center justify-end gap-2">
                                                    <Input
                                                        autoFocus
                                                        value={linking.externalId}
                                                        onChange={(e) =>
                                                            setLinking({
                                                                entityId: record.entityId,
                                                                externalId: e.target.value,
                                                            })
                                                        }
                                                        placeholder="Provider record id"
                                                        className="h-8 max-w-[220px] text-sm"
                                                    />
                                                    <Button
                                                        size="sm"
                                                        className="w-auto"
                                                        disabled={busy || !linking.externalId.trim()}
                                                        onClick={() =>
                                                            void handleLink(record.entityId, linking.externalId.trim())
                                                        }
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="w-auto"
                                                        onClick={() => setLinking(null)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="flex flex-wrap items-center justify-end gap-2">
                                                    {record.externalId ? (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="w-auto"
                                                            disabled={busy}
                                                            title={`Forget this mapping. The record in ${providerLabel} is left exactly as it is.`}
                                                            onClick={() => void handleUnlink(record.entityId)}
                                                        >
                                                            <Link2Off className="mr-1.5 h-3.5 w-3.5" />
                                                            Unlink
                                                        </Button>
                                                    ) : (
                                                        <>
                                                            <Button
                                                                size="sm"
                                                                className="w-auto"
                                                                disabled={busy}
                                                                onClick={() => requestEnsure([record.entityId])}
                                                            >
                                                                <Link2 className="mr-1.5 h-3.5 w-3.5" />
                                                                {busy ? "Connecting…" : "Connect"}
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="w-auto"
                                                                disabled={busy}
                                                                onClick={() =>
                                                                    setLinking({
                                                                        entityId: record.entityId,
                                                                        externalId: "",
                                                                    })
                                                                }
                                                            >
                                                                Link existing
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Connecting one assignment can create three records. Say which, by name. */}
            <Dialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {pending && pending.length > 1
                                ? `Connect ${counted(entityType, pending.length)}?`
                                : `Connect this ${NOUNS[entityType].one}?`}
                        </DialogTitle>
                        <DialogDescription>
                            {pendingRecord
                                ? `${pendingRecord.label}${pendingRecord.detail ? ` — ${pendingRecord.detail}` : ""} can't exist in ${providerLabel} on its own.`
                                : `Some of these can't exist in ${providerLabel} on their own.`}{" "}
                            These will be connected too:
                        </DialogDescription>
                    </DialogHeader>

                    <ul className="flex flex-col gap-2">
                        {prerequisites.map((group) => (
                            <li key={group.entityType} className="bg-muted/40 rounded-md px-3 py-2">
                                <Text weight="medium" className="text-sm">
                                    {counted(group.entityType, group.labels.length)}
                                </Text>
                                <Text className="text-muted-foreground text-xs">{nameList(group.labels)}</Text>
                            </li>
                        ))}
                    </ul>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPending(null)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (!pending) return;
                                const entityIds = pending;
                                setPending(null);
                                void runEnsure(entityIds);
                            }}
                        >
                            Connect
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
